/**
 * The brand a reader sees.
 *
 * Artifacts are read by the tier-1 user's OWN customers — a briefing on the oil
 * business goes out under the sender's name, not ours — so the public renderer
 * has to wear their identity. Until this screen existed the model, the API and
 * the renderer all honoured a brand and there was no way to create one, so
 * every shared artifact rendered unbranded.
 *
 * THE PREVIEW IS THE POINT. These fields are hex codes and URLs; nobody can
 * tell whether #1f6f6b on #ffffff reads well from the hex. The preview is a
 * real (small) render of the reader page, so the question the screen answers is
 * "what will they see" rather than "what did I type".
 *
 * One profile per end client, one of them default. The default is what an artifact
 * falls back to when its project names no override.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import type { BrandProfile } from '../types';

type Draft = Partial<BrandProfile> & { name: string };

const BLANK: Draft = {
  name: '',
  display_name: '',
  logo_url: '',
  primary_color: '',
  accent_color: '',
  background_color: '',
  text_color: '',
  font_family: '',
  footer_text: '',
  website_url: '',
};

/** Mirrors the fallbacks in the reader app — `frontend/reader/app/r/[token]/page.tsx`,
 *  `ArtifactBody`. If those change, change these: a preview that doesn't match the
 *  real page is worse than none.
 *
 *  It is a separate deployable, so this cannot be an import and nothing will
 *  fail if the two drift. The values are the reason to check. */
const FALLBACK = {
  bg: '#ffffff',
  fg: '#16201f',
  accent: '#1f6f6b',
  font: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
};

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

function ColorField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const valid = !value || HEX.test(value);
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 dark:text-gray-200">{label}</span>
      {hint && <span className="block text-xs text-gray-400 dark:text-gray-500 mb-1">{hint}</span>}
      <span className="flex items-center gap-2 mt-1">
        {/* The swatch is a real colour input, but it cannot express "unset" —
            so the text field stays authoritative and empty means "fall back". */}
        <input
          type="color"
          value={HEX.test(value) ? value.slice(0, 7) : '#ffffff'}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-10 rounded border border-gray-300 dark:border-gray-600 bg-transparent cursor-pointer shrink-0"
          aria-label={`${label} swatch`}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="unset"
          spellCheck={false}
          className={`flex-1 min-w-0 rounded-md border px-2 py-1.5 text-sm font-mono dark:bg-gray-700 dark:text-gray-100 ${
            valid
              ? 'border-gray-300 dark:border-gray-600'
              : 'border-red-400 dark:border-red-500'
          }`}
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 shrink-0"
            title="Clear - fall back to the default"
          >
            clear
          </button>
        )}
      </span>
      {!valid && (
        <span className="block text-xs text-red-600 dark:text-red-400 mt-1">
          Use a hex color like #1f6f6b.
        </span>
      )}
    </label>
  );
}

function TextField({
  label,
  hint,
  value,
  onChange,
  placeholder,
  multiline,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
}) {
  const cls =
    'mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 px-2 py-1.5 text-sm';
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 dark:text-gray-200">{label}</span>
      {hint && <span className="block text-xs text-gray-400 dark:text-gray-500">{hint}</span>}
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={placeholder} className={cls} />
      ) : (
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cls} />
      )}
    </label>
  );
}

/** A small, honest render of the reader page. */
function Preview({ d }: { d: Draft }) {
  const bg = (HEX.test(d.background_color || '') && d.background_color) || FALLBACK.bg;
  const fg = (HEX.test(d.text_color || '') && d.text_color) || FALLBACK.fg;
  const accent =
    (HEX.test(d.accent_color || '') && d.accent_color) ||
    (HEX.test(d.primary_color || '') && d.primary_color) ||
    FALLBACK.accent;
  const font = d.font_family || FALLBACK.font;

  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm"
      style={{ background: bg, color: fg, fontFamily: font }}
    >
      <div className="px-6 py-6">
        <div className="pb-4 mb-5" style={{ borderBottom: `2px solid ${accent}` }}>
          {d.logo_url ? (
            <img
              src={d.logo_url}
              alt=""
              className="h-7 mb-3"
              style={{ maxWidth: '180px', objectFit: 'contain' }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            d.display_name && (
              <p className="text-xs font-semibold mb-2 tracking-wide" style={{ color: accent }}>
                {d.display_name}
              </p>
            )
          )}
          <h1 className="text-xl font-bold leading-tight">Offshore wind, week 34</h1>
          <p className="mt-1.5 text-xs opacity-60">19 August 2026</p>
        </div>

        <article>
          <h2 className="text-base font-semibold leading-snug">
            Three operators shift maintenance in-house
          </h2>
          <p className="text-sm leading-relaxed mt-1.5">
            Two of the largest North Sea operators have ended third-party service contracts
            this quarter, citing vessel availability rather than cost.
          </p>
          <p className="text-xs leading-relaxed mt-2 pl-3" style={{ borderLeft: `3px solid ${accent}`, opacity: 0.85 }}>
            Why it matters: the service market everyone has been sizing may be shrinking from
            the top.
          </p>
          <p className="text-xs mt-3">
            <span style={{ color: accent }} className="underline underline-offset-2">
              Reuters - operators bring O&amp;M in-house
            </span>
          </p>
        </article>

        {(d.footer_text || d.website_url) && (
          <div className="mt-6 pt-4 text-xs opacity-60" style={{ borderTop: `1px solid ${accent}33` }}>
            {d.footer_text && <p className="whitespace-pre-line">{d.footer_text}</p>}
            {d.website_url && (
              <p className="mt-1" style={{ color: accent }}>
                {d.website_url.replace(/^https?:\/\//, '')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function BrandSettings() {
  const [brands, setBrands] = useState<BrandProfile[]>([]);
  const [selectedId, setSelectedId] = useState<number | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const load = useCallback(async (selectAfter?: number) => {
    setLoading(true);
    try {
      const rows = await api.listBrandProfiles();
      setBrands(rows);
      const pick = selectAfter ?? rows.find((b) => b.is_default)?.id ?? rows[0]?.id ?? null;
      setSelectedId(pick);
      const row = rows.find((b) => b.id === pick);
      if (row) setDraft({ ...BLANK, ...stripNulls(row) });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load brands');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const select = (id: number) => {
    const row = brands.find((b) => b.id === id);
    if (!row) return;
    setSelectedId(id);
    setDraft({ ...BLANK, ...stripNulls(row) });
    setSaved(false);
    setError('');
  };

  const startNew = () => {
    setSelectedId('new');
    setDraft({ ...BLANK, name: '' });
    setSaved(false);
    setError('');
  };

  const set = (k: keyof Draft) => (v: string) => {
    setDraft((d) => ({ ...d, [k]: v }));
    setSaved(false);
  };

  const save = async () => {
    if (!draft.name.trim()) {
      setError('Give the brand a name so you can tell it from the others.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Empty string means "unset", which is null on the wire — the renderer
      // falls back per field, so a logo with no colours is a valid brand.
      const payload: any = { name: draft.name.trim() };
      for (const k of [
        'display_name', 'logo_url', 'primary_color', 'accent_color',
        'background_color', 'text_color', 'font_family', 'footer_text', 'website_url',
      ] as const) {
        const v = (draft[k] as string | undefined)?.trim();
        payload[k] = v ? v : null;
      }
      const row =
        selectedId === 'new'
          ? await api.createBrandProfile(payload)
          : await api.updateBrandProfile(selectedId as number, payload);
      setSaved(true);
      await load(row.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const makeDefault = async () => {
    if (typeof selectedId !== 'number') return;
    setError('');
    try {
      await api.updateBrandProfile(selectedId, { is_default: true });
      await load(selectedId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not set default');
    }
  };

  const remove = async () => {
    if (typeof selectedId !== 'number') return;
    const row = brands.find((b) => b.id === selectedId);
    if (!window.confirm(`Delete "${row?.name}"? Projects using it fall back to the default.`)) return;
    setError('');
    try {
      await api.deleteBrandProfile(selectedId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete');
    }
  };

  const current = typeof selectedId === 'number' ? brands.find((b) => b.id === selectedId) : null;

  if (loading) return <p className="text-sm text-gray-400 p-4">Loading…</p>;

  return (
    <div className="p-1">
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 max-w-2xl">
        What a reader sees when you share a briefing, a page, or a recon digest. Artifacts go
        out under <em>your</em> name, not ours. A subject can override the default with its own
        brand, so one account can serve several clients.
      </p>

      {brands.length === 0 && selectedId !== 'new' && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            No brands yet, so shared artifacts render unbranded.
          </p>
          <button onClick={startNew} className="px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700">
            Create your first brand
          </button>
        </div>
      )}

      {(brands.length > 0 || selectedId === 'new') && (
        <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)_minmax(0,22rem)]">
          {/* profiles */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Brands
              </h3>
              <button onClick={startNew} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                + New
              </button>
            </div>
            <ul className="flex flex-col gap-1">
              {brands.map((b) => (
                <li key={b.id}>
                  <button
                    onClick={() => select(b.id)}
                    className={`w-full text-left px-2.5 py-2 rounded-md text-sm flex items-center gap-2 ${
                      selectedId === b.id
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 font-medium'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    <span
                      className="w-3 h-3 rounded-sm shrink-0 border border-black/10"
                      style={{ background: b.accent_color || b.primary_color || FALLBACK.accent }}
                    />
                    <span className="truncate flex-1">{b.name}</span>
                    {b.is_default && (
                      <span className="text-[10px] uppercase tracking-wider text-gray-400 shrink-0">default</span>
                    )}
                  </button>
                </li>
              ))}
              {selectedId === 'new' && (
                <li className="px-2.5 py-2 rounded-md text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 font-medium">
                  New brand…
                </li>
              )}
            </ul>
          </div>

          {/* editor */}
          <div className="flex flex-col gap-4 min-w-0">
            <TextField
              label="Name"
              hint="Only you see this - it's how you tell brands apart."
              value={draft.name}
              onChange={set('name')}
              placeholder="Acme Energy"
            />
            <TextField
              label="Shown to readers"
              hint="Used when there's no logo."
              value={draft.display_name || ''}
              onChange={set('display_name')}
              placeholder="Acme Energy Research"
            />
            <TextField
              label="Logo URL"
              hint="Must be publicly reachable - the reader isn't logged in."
              value={draft.logo_url || ''}
              onChange={set('logo_url')}
              placeholder="https://…/logo.png"
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <ColorField label="Accent" hint="Rules, links, labels." value={draft.accent_color || ''} onChange={set('accent_color')} />
              <ColorField label="Primary" hint="Falls back to accent." value={draft.primary_color || ''} onChange={set('primary_color')} />
              <ColorField label="Background" value={draft.background_color || ''} onChange={set('background_color')} />
              <ColorField label="Text" value={draft.text_color || ''} onChange={set('text_color')} />
            </div>

            <TextField
              label="Font family"
              hint="A CSS stack. Web-safe fonts only - the page loads no external files."
              value={draft.font_family || ''}
              onChange={set('font_family')}
              placeholder='Georgia, "Times New Roman", serif'
            />
            <TextField label="Footer" value={draft.footer_text || ''} onChange={set('footer_text')} multiline placeholder="Acme Energy Research · Reply to unsubscribe" />
            <TextField label="Website" value={draft.website_url || ''} onChange={set('website_url')} placeholder="https://acme.example" />

            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

            <div className="flex items-center gap-3 flex-wrap pt-1">
              <button
                onClick={save}
                disabled={saving}
                className="px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : selectedId === 'new' ? 'Create brand' : 'Save'}
              </button>
              {saved && <span className="text-sm text-green-600 dark:text-green-400">Saved</span>}
              {current && !current.is_default && (
                <button onClick={makeDefault} className="text-sm text-gray-600 dark:text-gray-300 hover:underline">
                  Make default
                </button>
              )}
              <div className="flex-1" />
              {current && (
                <button onClick={remove} className="text-sm text-red-600 dark:text-red-400 hover:underline">
                  Delete
                </button>
              )}
            </div>
          </div>

          {/* preview */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              What a reader sees
            </h3>
            <Preview d={draft} />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
              Sample content. The real page uses the same colors, fonts, and fallbacks.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/** null -> '' so the inputs stay controlled. */
function stripNulls(row: BrandProfile): Partial<Draft> {
  const out: any = {};
  for (const [k, v] of Object.entries(row)) out[k] = v == null ? '' : v;
  out.id = row.id;
  out.is_default = row.is_default;
  return out;
}
